import { EsCvData } from '@/shared/cv/cv.es.dto';
import { InsightDataDto } from '@/shared/job/insight.dto';

/**
 * Төлөв:
 * - match     ✓ Нийцсэн
 * - advantage ▲ Давуу
 * - lacking   ▼ Дутуу
 * - missing   ⓘ Мэдээлэл дутуу (CV хоосон)
 */
export type InsightCompareStatus =
  | 'match'
  | 'advantage'
  | 'lacking'
  | 'missing';

/** Мөрийн нэг хэсэг. `bold` = тодруулж харуулах хэсэг. */
export type InsightComparePart = { text: string; bold?: boolean };

export type InsightCompareItem = {
  key: 'experience' | 'education' | 'skills';
  status: InsightCompareStatus;
  parts: InsightComparePart[];
};

const t = (text: string): InsightComparePart => ({ text });
const b = (text: string): InsightComparePart => ({ text, bold: true });

/**
 * Боловсролын түвшний id → зэрэглэл (их тоо = өндөр түвшин).
 * Id-ууд нь insight `edu` жагсаалттай ижил.
 */
export const DEGREE_LEVELS: Record<
  number,
  { rank: number; mn: string; en: string }
> = {
  7: { rank: 1, mn: 'Бага', en: 'Primary School' },
  6: { rank: 2, mn: 'Бүрэн бус дунд', en: 'Less than High School' },
  5: { rank: 3, mn: 'Бүрэн дунд', en: 'High School' },
  4: { rank: 4, mn: 'Тусгай дунд', en: 'Associate degree' },
  8: { rank: 5, mn: 'Мэргэшсэн', en: 'Certified diploma' },
  1: { rank: 6, mn: 'Бакалавр', en: 'Bachelor' },
  2: { rank: 7, mn: 'Магистр', en: 'Master' },
  3: { rank: 8, mn: 'Доктор', en: 'Ph.D' },
};

/** Туршлагыг "яг таарсан" гэж үзэх зөрүү (сар). */
export const EXPERIENCE_TOLERANCE_MONTHS = 1;
/** Ур чадварын "нийцсэн" босго. */
export const SKILLS_MATCH_RATIO = 0.75;

const MS_PER_MONTH = 1000 * 60 * 60 * 24 * 30.4375;

/** CV-д мэдээлэл байхгүй үеийн (ⓘ) мөр. */
function missingItem(
  key: InsightCompareItem['key'],
  isEnglish: boolean
): InsightCompareItem {
  const parts: Record<
    InsightCompareItem['key'],
    { mn: InsightComparePart[]; en: InsightComparePart[] }
  > = {
    experience: {
      mn: [
        b('Ажлын туршлагаа'),
        t(' CV-дээ нэмж оруулснаар тодорхой харагдана'),
      ],
      en: [
        t('Add your '),
        b('work experience'),
        t(' to your CV to see a clearer match'),
      ],
    },
    education: {
      mn: [b('Боловсролоо'), t(' CV-дээ нэмж оруулснаар тодорхой харагдана')],
      en: [
        t('Add your '),
        b('education'),
        t(' to your CV to see a clearer match'),
      ],
    },
    skills: {
      mn: [b('Ур чадвараа'), t(' CV-дээ нэмж оруулснаар тодорхой харагдана')],
      en: [
        t('Add your '),
        b('skills'),
        t(' to your CV to see a clearer match'),
      ],
    },
  };
  return {
    key,
    status: 'missing',
    parts: isEnglish ? parts[key].en : parts[key].mn,
  };
}

function degreeName(id: number, isEnglish: boolean): string {
  const level = DEGREE_LEVELS[id];
  if (!level) return String(id);
  return isEnglish ? level.en : level.mn;
}

/** Хэрэглэгчийн CV дэх хамгийн өндөр боловсролын түвшин (id) болон бүх id. */
function getUserDegrees(cv: EsCvData): {
  ids: Set<number>;
  highestId: number | null;
} {
  const ids = new Set<number>();
  let highestId: number | null = null;

  for (const edu of cv.education ?? []) {
    const level = DEGREE_LEVELS[edu.degreeId];
    if (!level) continue;
    ids.add(edu.degreeId);
    if (highestId === null || level.rank > DEGREE_LEVELS[highestId].rank) {
      highestId = edu.degreeId;
    }
  }
  return { ids, highestId };
}

function hasExperienceData(cv: EsCvData): boolean {
  return (
    (typeof cv.totalYear === 'number' && cv.totalYear > 0) ||
    (cv.experience ?? []).length > 0
  );
}

/** Нийт ажилласан хугацаа (сараар). `totalYear` байхгүй бол туршлагын жагсаалтаас тооцно. */
export function getUserExperienceMonths(cv: EsCvData): number {
  if (typeof cv.totalYear === 'number' && cv.totalYear > 0) {
    return Math.round(cv.totalYear * 12);
  }

  // Туршлага хэл тус бүрээр (mn/en) давхардан хадгалагддаг тул нэг хэлийг авна
  const experiences = cv.experience ?? [];
  if (experiences.length === 0) return 0;
  const langId = experiences[0].langId;
  const now = Date.now();

  const intervals = experiences
    .filter((exp) => exp.langId === langId)
    .map((exp) => {
      const start = exp.startOn ? new Date(exp.startOn).getTime() : NaN;
      const end = exp.endOn ? new Date(exp.endOn).getTime() : now;
      return { start, end };
    })
    .filter(({ start, end }) => !Number.isNaN(start) && !Number.isNaN(end))
    .filter(({ start, end }) => end > start)
    .sort((a, b) => a.start - b.start);

  // Давхцсан хугацааг нэг удаа тоолно
  let totalMs = 0;
  let curStart = 0;
  let curEnd = 0;
  intervals.forEach(({ start, end }, index) => {
    if (index === 0) {
      curStart = start;
      curEnd = end;
      return;
    }
    if (start <= curEnd) {
      curEnd = Math.max(curEnd, end);
      return;
    }
    totalMs += curEnd - curStart;
    curStart = start;
    curEnd = end;
  });
  if (intervals.length > 0) totalMs += curEnd - curStart;

  return Math.round(totalMs / MS_PER_MONTH);
}

/**
 * Хугацааг текст болгоно (зөрүү: "1 жил 3 сараар", англи: "1 year 3 months").
 */
function formatDiff(months: number, isEnglish: boolean): string {
  const years = Math.floor(months / 12);
  const rest = months % 12;

  if (isEnglish) {
    const parts: string[] = [];
    if (years > 0) parts.push(`${years} ${years === 1 ? 'year' : 'years'}`);
    if (rest > 0) parts.push(`${rest} ${rest === 1 ? 'month' : 'months'}`);
    return parts.length > 0 ? parts.join(' ') : '0 months';
  }
  if (years > 0 && rest > 0) return `${years} жил ${rest} сараар`;
  if (years > 0) return `${years} жилээр`;
  return `${rest} сараар`;
}

function formatExpRange(
  min: number,
  max: number | null,
  isEnglish: boolean
): string {
  if (max !== null && max === min) {
    return isEnglish ? `${min} years` : `${min} жил`;
  }
  if (max !== null && min > 0) {
    return isEnglish ? `${min}–${max} years` : `${min}–${max} жил`;
  }
  if (max !== null) {
    return isEnglish ? `up to ${max} years` : `${max} жил хүртэл`;
  }
  return isEnglish ? `${min}+ years` : `${min}+ жил`;
}

/** Ажлын туршлага: зарын шаардлага (min–max жил) vs хэрэглэгчийн нийт туршлага. */
function compareExperience(
  data: InsightDataDto,
  cv: EsCvData,
  isEnglish: boolean
): InsightCompareItem | null {
  const req = data.required_exp;
  if (!req) return null;

  const min = Math.max(0, req.min ?? 0);
  const hasMax = (req.max ?? 0) > 0 && req.max >= min;
  const max = hasMax ? req.max : null;

  // 4. Туршлага шаардахгүй
  if (min === 0 && max === null) {
    return {
      key: 'experience',
      status: 'match',
      parts: isEnglish
        ? [t('This job '), b('requires no experience')]
        : [t('Энэ ажилд '), b('туршлага шаардахгүй')],
    };
  }

  // 5. CV хоосон
  if (!hasExperienceData(cv)) return missingItem('experience', isEnglish);

  const userMonths = getUserExperienceMonths(cv);
  const lowerMonths = min * 12;
  // Дээд хязгааргүй бол шаардлагыг нэг цэг (min) гэж үзнэ
  const upperMonths = (max ?? min) * 12;

  // 3. Дутуу
  if (userMonths < lowerMonths - EXPERIENCE_TOLERANCE_MONTHS) {
    const diff = formatDiff(lowerMonths - userMonths, isEnglish);
    return {
      key: 'experience',
      status: 'lacking',
      parts: isEnglish
        ? [t('Work experience is '), b(diff), t(' below the requirement')]
        : [t('Ажлын туршлага шаардлагаас '), b(diff), t(' дутуу')],
    };
  }

  // 2. Илүү
  if (userMonths > upperMonths + EXPERIENCE_TOLERANCE_MONTHS) {
    const diff = formatDiff(userMonths - upperMonths, isEnglish);
    return {
      key: 'experience',
      status: 'advantage',
      parts: isEnglish
        ? [t('Work experience is '), b(diff), t(' above the requirement')]
        : [t('Ажлын туршлага шаардлагаас '), b(diff), t(' илүү')],
    };
  }

  // 1. Нийцсэн
  const range = formatExpRange(min, max, isEnglish);
  return {
    key: 'experience',
    status: 'match',
    parts: isEnglish
      ? [t('Work experience meets the job requirement ('), b(range), t(')')]
      : [t('Ажлын туршлага зарын шаардлагад ('), b(range), t(') нийцэж байна')],
  };
}

/** Боловсрол: зарын шаардлага (degree_ids) vs хэрэглэгчийн боловсрол. */
function compareEducation(
  data: InsightDataDto,
  cv: EsCvData,
  isEnglish: boolean
): InsightCompareItem | null {
  const required = (data.degree_ids ?? []).filter((id) => DEGREE_LEVELS[id]);
  if (required.length === 0) return null;

  const { ids, highestId } = getUserDegrees(cv);

  // 9. CV хоосон
  if (highestId === null) return missingItem('education', isEnglish);

  const requiredRanks = required.map((id) => DEGREE_LEVELS[id].rank);
  const minRank = Math.min(...requiredRanks);
  const maxRank = Math.max(...requiredRanks);
  const requiredNames = [...required]
    .sort((x, y) => DEGREE_LEVELS[x].rank - DEGREE_LEVELS[y].rank)
    .map((id) => degreeName(id, isEnglish))
    .join(', ');

  const userRank = DEGREE_LEVELS[highestId].rank;
  const userName = degreeName(highestId, isEnglish);
  const prefix = t(isEnglish ? 'Education — ' : 'Боловсрол — ');

  // 7. Дээгүүр
  if (userRank > maxRank) {
    return {
      key: 'education',
      status: 'advantage',
      parts: isEnglish
        ? [prefix, b(userName), t(', above the requirement ('), b(requiredNames), t(')')]
        : [prefix, b(userName), t(', шаардлагаас ('), b(requiredNames), t(') дээгүүр')],
    };
  }

  // 6. Таарсан
  if (required.some((id) => ids.has(id)) || userRank >= minRank) {
    return {
      key: 'education',
      status: 'match',
      parts: [
        prefix,
        b(userName),
        t(
          isEnglish
            ? ', meets the job requirement'
            : ', зарын шаардлагад нийцэж байна'
        ),
      ],
    };
  }

  // 8. Доогуур
  return {
    key: 'education',
    status: 'lacking',
    parts: [
      prefix,
      b(userName),
      t(isEnglish ? ', requirement: ' : ', шаардлага '),
      b(requiredNames),
    ],
  };
}

/**
 * Ур чадвар: зарт хавсаргасан ур чадвар (`jobSkills` — skill id-ууд)
 * vs хэрэглэгчийн CV дэх ур чадвар.
 * (`compatibility` нь зарт илгээсэн горилогчдын ур чадварын давхардал тул энд ашиглахгүй.)
 */
function compareSkills(
  data: InsightDataDto,
  cv: EsCvData,
  isEnglish: boolean
): InsightCompareItem | null {
  const requiredIds = [...new Set(data.jobSkills ?? [])];
  if (requiredIds.length === 0) return null;

  const owned = new Set<number>();
  for (const skill of cv.skills ?? []) {
    const id = skill.skillid ?? skill.skillId;
    if (id != null) owned.add(id);
  }

  // 15. CV хоосон
  if (owned.size === 0) return missingItem('skills', isEnglish);

  const total = requiredIds.length;
  const matched = requiredIds.filter((id) => owned.has(id)).length;

  // 14. Огт байхгүй
  if (matched === 0) {
    return {
      key: 'skills',
      status: 'lacking',
      parts: isEnglish
        ? [t('None of the required skills are '), b('in your CV')]
        : [t('Шаардлагатай ур чадварууд таны '), b('CV-д алга')],
    };
  }

  // 10. Бүгд байгаа
  if (matched === total) {
    return {
      key: 'skills',
      status: 'match',
      parts: isEnglish
        ? [
            t('You have all '),
            b(`${total} required ${total === 1 ? 'skill' : 'skills'}`),
          ]
        : [t('Шаардлагатай '), b(`${total} ур чадвар`), t(' бүгд танд байна')],
    };
  }

  // 12. ≥75%  /  13. <75%
  return {
    key: 'skills',
    status: matched / total >= SKILLS_MATCH_RATIO ? 'match' : 'lacking',
    parts: isEnglish
      ? [
          t('You have '),
          b(`${matched} of ${total}`),
          t(' required skills'),
        ]
      : [
          b(`${total} ур чадвараас`),
          t(' танд '),
          b(`${matched}`),
          t(' байна'),
        ],
  };
}

/** Insight өгөгдөл болон хэрэглэгчийн CV-г харьцуулсан дүгнэлтүүд. */
export function buildInsightCompareItems(
  data: InsightDataDto,
  cv: EsCvData,
  isEnglish: boolean
): InsightCompareItem[] {
  return [
    compareExperience(data, cv, isEnglish),
    compareEducation(data, cv, isEnglish),
    compareSkills(data, cv, isEnglish),
  ].filter((item): item is InsightCompareItem => item !== null);
}
